import { observer } from "mobx-react-lite"
import Layout from "../components/layout/Layout"
import { useStore } from "../stores/root-store-context";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Flex, Image, Row, Col, Card } from "antd";
import { SettingOutlined } from '@ant-design/icons';
import dayjs from "dayjs";
import { useEffect } from "react";
import { Roles } from "../enums/Roles";
import { TariffPlan } from "../enums/TariffPlan";

const CompanyHome = observer(() => {
    const { applicationStore, paymentStore } = useStore();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const isTrialPeriod = dayjs(paymentStore.trialDate).isSame(dayjs(paymentStore.paymentDate));
    const dateMessage = isTrialPeriod
        ? `${t('trialPeriodEnds')} ${dayjs(paymentStore.trialDate).format('YYYY.MM.DD')}`
        : `${t('nextPaymentDate')} ${dayjs(paymentStore.paymentDate).format('YYYY.MM.DD')}`;

useEffect(() => {
    if (applicationStore.company?.Id) {
        paymentStore.getPaymentDate(applicationStore.company.Id);
    }
}, [applicationStore.company, paymentStore]);

    // Define service cards data
    const serviceCards = [
        // User Profile Card
        {
            id: 1,
            icon: "/assets/img/service/icon/s-icon-1.svg",
            title: applicationStore.user.email,
            description: t("ViewAndEditProfile"),
            linkText: t("Open_Your_Profile"),
            linkUrl: `/${applicationStore.company.Tenant}/employees/${applicationStore.user.id}`
        },
        // Employees Card
        {
            id: 2,
            icon: "/assets/img/service/icon/s-icon-9.svg",
            title: <><span>{applicationStore.company.UsersCount}</span> {t("Employees")}</>,
            description: t("ViewAllCompanyEmployeeProfiles"),
            linkText: t("ViewAllUsers"),
            linkUrl: `/${applicationStore.company.Tenant}/employees`
        },
        // Projects Card
        {
            id: 3,
            icon: "/assets/img/service/icon/s-icon-10.svg",
            title: <><span>{applicationStore.company.ProjectCount}</span> {t("Projectses")}</>,
            description: t("ProjectsOfYourCompany"),
            linkText: t("ViewAllProjects"),
            linkUrl: `/${applicationStore.company.Tenant}/projects`
        }
    ];

    // Add API card if user is Owner
    if (applicationStore.user?.roles?.some(r => r.id == Roles.Owner || r.id == Roles.Admin)) {
        serviceCards.push({
            id: 4,
            icon: "/assets/img/service/icon/s-icon-11.svg",
            title: t("IntegrationAPI"),
            description: t("AccessApiDocumentationAndIntegration"),
            linkText: t("ViewApiDocumentation"),
            linkUrl: `/${applicationStore.company.Tenant}/api`
        });
    }





    // Add Skills testing card if not on Basic or Annual plan
    if (applicationStore.company.TariffPlanId != TariffPlan.Basic && applicationStore.company.TariffPlanId != TariffPlan.Annual) {
        serviceCards.push({
            id: 5,
            icon: "/assets/img/service/icon/s-icon-12.svg",
            title: t("Skill_testing"),
            description: t("Approve_your_skills"),
            linkText: t("Approve_your_skills"),
            linkUrl: `/${applicationStore.company.Tenant}/tests`
        });
    }
    if (applicationStore.company.TariffPlanId != TariffPlan.Basic && applicationStore.company.TariffPlanId != TariffPlan.Annual && applicationStore.user?.roles?.some(r => r.id == Roles.Owner || r.id == Roles.Financier)) {
        serviceCards.push({
            id: 6,
            icon: "/assets/img/service/icon/s-icon-13.svg",
            title: t("Mini_ERP"),
            description: t("Mini_ERP_Description"),
            linkText: t("OpenMiniErp"),
            linkUrl: `/${applicationStore.company.Tenant}/erp/finances`
        });

                serviceCards.push({
            id: 7,
            icon: "/assets/img/service/icon/s-icon-7.svg",
            title: t("Mini_CRM"),
            description: t("Mini_CRM_Description"),
            linkText: t("OpenMiniCrm"),
            linkUrl: `/${applicationStore.company.Tenant}/crm`
        });
    }

    if (applicationStore.company.SupportSystem) {
        serviceCards.push({
            id: 8,
            icon: "/assets/img/service/icon/chat-support.svg",
            title: t("SupportSystem"),
            description: t("AccessSupportResources"),
            linkText: t("OpenSupport"),
            linkUrl: `/support-system`
        });
    }


    return (
        <Layout headerStyle={1} footerStyle={2} >
            <div className="text-center" style={{ margin: "100px 0" }}>
            </div>

            <section className="service-section fix section-padding bg-cover" style={{ backgroundImage: 'url("assets/img/service/service-bg.jpg")', marginBottom: '20px' }} id="service">
                <div className="container">
                    <Flex style={{ width: "100%" }} justify="flex-end">
                        {(applicationStore.user?.roles?.some(r => r.id == Roles.Owner || r.id == Roles.Admin)) &&
                            <Button size="large" type="primary" shape="circle" icon={<SettingOutlined />}
                                onClick={() => {
                                    navigate(`/${applicationStore.company.Tenant}/settings`);
                                }} />
                        }
                    </Flex>
                    {
                        applicationStore?.company.LogoId &&
                        <Flex justify="center" style={{ marginBottom: '20px' }}>
                            <Image
                                height={160}
                                style={{ objectFit: "cover", minWidth: 160 }}
                                src={
                                    `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/${applicationStore?.company.LogoId}`
                                }
                            />
                        </Flex>
                    }

                    <div className="section-title-area text-center">
                        <div className="section-title col-lg-4">
                            <span className="wow fadeInUp">{t("CompanyName")}</span>
                            <h3 className="wow fadeInUp" data-wow-delay=".3s">
                                {applicationStore.company.Title}
                            </h3>
                        </div>
                        <div className="section-title col-lg-4">
                            <span className="wow fadeInUp">{t("TenantName")}</span>
                            <h3 className="wow fadeInUp" data-wow-delay=".3s">
                                {applicationStore.company.Tenant}
                            </h3>
                        </div>
                        <div className="section-title col-lg-4">
                            <span className="wow fadeInUp">{t("Description")}</span>
                            <h4 className="wow fadeInUp" data-wow-delay=".3s">
                                {applicationStore.company.Description}
                            </h4>
                        </div>
                    </div>

                    <div className="service-wrapper">
                        {/* Grid Layout for Service Cards */}
                        <Row gutter={[24, 24]} className="service-grid">
                            {serviceCards.map(card => (
                                <Col key={card.id} xs={24} sm={12} md={8} lg={6} xl={card.id === 1 ? 6 : 6}>
                                    <Link to={card.linkUrl} style={{ display: 'block', height: '100%' }}>
                                        <div className="service-box-items" style={{ height: '100%', cursor: 'pointer' }}>
                                            <div className="icon">
                                                <img src={card.icon} alt="icon-img" style={{ width: '80px', height: '80px' }}/>
                                            </div>
                                            <div className="content">
                                                <h4 style={{ textTransform: "none", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {card.title}
                                                </h4>
                                                <p>
                                                    {card.description}
                                                </p>
                                                <div className="theme-btn-2 mt-3">
                                                    {card.linkText}
                                                    <i className="fa-solid fa-arrow-right-long" />
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </Col>
                            ))}
                        </Row>

                        {/* Notification Message */}
                        {(applicationStore.user?.roles?.some(r => r.id == Roles.Owner|| r.id == Roles.Admin) && applicationStore.company.TariffPlanId !== 5) && (
                            <div className="notification-message" style={{
                                textAlign: 'center',
                                backgroundColor: '#ffffff',
                                padding: '20px',
                                marginTop: '20px',
                                fontSize: '18px',
                                fontWeight: 'bold',
                                color: '#0F0D1D'
                            }}>
                                {dateMessage}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </Layout>
    )
})

export default CompanyHome;